'use client';
import { Box, Card, Loading, Stack, Tooltip, Typography } from '@castlery/fortress';
import { Info } from '@castlery/fortress/Icons';
import { useGetVouchersV1Query } from '@castlery/modules-user-domain';
import { TooltipEllipsis } from '@castlery/shared-components';
import { formatDate, isOutdated, toPrice } from '@castlery/utils';
import { useEffect, useState } from 'react';
import { formatPreferences } from './helper';
import VoucherBanner from './voucherBanner';

export function VouchersPageV1() {
  const { data, isLoading, error } = useGetVouchersV1Query(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [vouchers, setVouchers] = useState<any[]>([]);

  const formatVoucher = (vouchers: any) =>
    vouchers.reduce((acc: any, cur: any) => {
      const { calculators } = cur;
      if (calculators?.length > 0) {
        const newCal = calculators.map((item: any) => {
          const { type, preferences } = item || {};
          const { title, description, descriptionInfo } = formatPreferences(type, preferences);
          return {
            ...item,
            title,
            description,
            descriptionInfo,
          };
        });

        const title = newCal.map((item: any) => item.title)?.join(' & ');
        acc.push({
          ...cur,
          calculators: newCal,
          title,
          description: newCal.length > 1 ? 'Combined Discount' : newCal[0]?.description,
          descriptionInfo: newCal.length > 1 ? title : newCal[0]?.descriptionInfo,
        });
      } else {
        acc.push(cur);
      }
      return acc;
    }, []);

  useEffect(() => {
    if (data) {
      console.log('data?.data?.vouchers', data);
      const vouchers = formatVoucher(data);
      console.log('vouchers', vouchers);
      setVouchers(vouchers);
    }
  }, [data]);

  const stateText = (voucher: any) => {
    // state 0: available, 1: unavailable, 2: expired, 未开始不返回
    const expired = isOutdated(voucher.expired_at);
    if (expired) {
      return `Expired on ${formatDate(voucher.expired_at)}`;
    } else {
      return `Valid through ${formatDate(voucher.expired_at)}`;
    }
  };

  const renderVoucherCard = (voucher: any) => {
    const { expired_at, description, min_spend, descriptionInfo } = voucher;
    const isExpired = isOutdated(expired_at); //0: available, 1: unavailable, 2: expired, 3: used

    let descTooltipInfo: any = '';
    if (description === 'Combined Discount') {
      descTooltipInfo = 'Combined Discount';
    } else if (min_spend > 0 && !description) {
      descTooltipInfo = `Min. spend ${toPrice(min_spend)}`;
    } else if (description === 'Tiered Discount') {
      descTooltipInfo = descriptionInfo;
    }

    return (
      <Card
        key={voucher.code}
        variant="outlined"
        sx={{
          paddingTop: '24px',
          position: 'relative',
          opacity: isExpired ? 0.4 : 1,
          transition: 'opacity 0.3s ease',
          width: '317px',
          height: '204px',
          borderRadius: '8px',
          border: '1px solid var(--fortress-palette-brand-mono-200)',
          backgroundColor: 'var(--fortress-palette-brand-warmLinen-200)',
          // 分割线
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '0',
            right: '0',
            top: '105px',
            height: '1px',
            borderBottom: '1px dashed var(--fortress-palette-brand-mono-300)',
            zIndex: 1,
          },
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="114"
          height="71"
          viewBox="0 0 114 71"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 1,
          }}
        >
          <path
            d="M113.344 0.620605C101.612 40.7001 55.3801 70.6206 0.131836 70.6206L0.130859 70.6196V9.62061C0.130859 4.65004 4.1603 0.620605 9.13086 0.620605H113.344Z"
            fill="var(--fortress-palette-brand-warmLinen-500)"
          />
        </svg>

        {/* 左侧圆形装饰 */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '107px',
            width: '22.6px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: 'var(--fortress-palette-brand-warmLinen-200)',
            border: '1px solid var(--fortress-palette-brand-mono-300)',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-1px',
              bottom: '-1px',
              left: '-1px',
              width: '50%',
              backgroundColor: 'var(--fortress-palette-brand-warmLinen-200)',
            },
          }}
        />

        {/* 右侧圆形装饰 */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '107px',
            width: '22.6px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: 'var(--fortress-palette-brand-warmLinen-200)',
            border: '1px solid var(--fortress-palette-brand-mono-300)',
            transform: 'translate(50%, -50%)',
            zIndex: 2,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-1px',
              bottom: '-1px',
              right: '-1px',
              width: '50%',
              backgroundColor: 'var(--fortress-palette-brand-warmLinen-200)',
            },
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 3,
            width: '24px',
            height: '24px',
          }}
        >
          {descTooltipInfo && (
            <Tooltip
              title={
                Array.isArray(descTooltipInfo) ? (
                  <Stack spacing={1}>
                    {descTooltipInfo.map((line: string) => (
                      <Box key={line}>{line}</Box>
                    ))}
                  </Stack>
                ) : (
                  descTooltipInfo
                )
              }
              placement="top"
              arrow
              theme="dark"
              enterTouchDelay={0}
              leaveTouchDelay={500}
            >
              <Box>
                <Info width={16} height={16} sx={{ color: 'var(--fortress-palette-netural-900)' }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            zIndex: 2,
            mt: 1,
          }}
        >
          <Box sx={{ width: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
            <TooltipEllipsis title={voucher?.title} placement="top" hideIcon>
              <Typography
                level="h3"
                sx={{
                  width: '90%',
                  wordBreak: 'keep-all',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '30px !important',
                }}
              >
                {voucher?.title}
              </Typography>
            </TooltipEllipsis>
          </Box>

          <Typography
            level="caption1"
            sx={{ mt: 1, fontSize: '16px !important', color: 'var(--fortress-palette-brand-mono-700)' }}
          >
            {stateText(voucher)}
          </Typography>
        </Box>

        <Box sx={{ position: 'absolute', bottom: 35, left: 0, width: '100%', textAlign: 'center' }}>
          <Typography
            level="caption1"
            sx={{ color: 'var(--fortress-palette-brand-mono-700)', fontSize: '16px !important' }}
          >
            Use code:
          </Typography>
          <Typography
            level="h4"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: 'center',
              fontSize: '24px !important',
            }}
          >
            {voucher.code}
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <Stack spacing={8}>
      <Typography level="h2">My Vouchers</Typography>
      <VoucherBanner />

      {error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography level="body1" sx={{ color: 'text.secondary' }}>
            {typeof error === 'string' ? error : 'An error occurred while loading vouchers'}
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', width: '100%' }}>
          <Loading theme="dark" />
        </Box>
      ) : vouchers.length === 0 ? (
        <Typography level="body1" sx={{ mt: 6 }}>
          You have no vouchers right now.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 317px)',
              gap: {
                xs: 4,
                sm: 6,
                md: 8,
              },
            }}
          >
            {vouchers.map(renderVoucherCard)}
          </Box>
          <Typography level="body1">Voucher code can be applied at cart.</Typography>
        </>
      )}
    </Stack>
  );
}
